import React, { useEffect, useState } from "react";
import {
  NavLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { ReactComponent as HpgclIcon } from "../../icons/cerc-dashboard.svg";
import { ReactComponent as BankingAalytics } from "../../icons/dashboard/bankingAnalytics.svg";
import { ReactComponent as ConsumptionAndGenerationAnalytics } from "../../icons/dashboard/consumptionAndGenerationAnalytics.svg";
import { ReactComponent as DemandForecasting } from "../../icons/dashboard/demandForecasting.svg";
import { ReactComponent as DiscomAnalysis } from "../../icons/dashboard/discomAnalysis.svg";
import { ReactComponent as ExchangeAnalysis } from "../../icons/dashboard/exchangeAnalysis.svg";
import { ReactComponent as GridFrequencyProfile } from "../../icons/dashboard/gridFrequencyProfile.svg";
import { ReactComponent as MarketMonitoring } from "../../icons/dashboard/marketmonitoring.svg";
import { ReactComponent as PowerAtlas } from "../../icons/dashboard/powerAtlas.svg";
import { ReactComponent as PriceForecasting } from "../../icons/dashboard/priceForecasting.svg";
import { ReactComponent as Upload } from "../../icons/upload.svg";

import { getSessionValidity } from "../../Rest_api/RestApi";
import { COLOR_CODES, isMobile } from "../../common";
import { RuvnlTitle } from "./components";
import { ReactComponent as AnalyticsIcon } from "../../icons/analytics.svg";
import { ReactComponent as AnalyticsSelectedIcon } from "../../icons/analytics-selected.svg";
import { ReactComponent as DashboardIcon } from "../../icons/dashboard.svg";
import { ReactComponent as DashboardSelectedIcon } from "../../icons/dashboard-selected.svg";
import { OverviewDas } from "./Overview/OverviewDas";
import { DynamicDatePicker } from "../../components/DatePicker";
import Hamburger from "../../components/Hamburger";
import { ReactComponent as RajAgentIcon } from "../../icons/gnai.svg";
import { ReactComponent as RajAgentSelectedIcon } from "../../icons/gnai-active.svg";
import RajAgent from "./Agent/Agent";
import { redirectToLogin } from "../Protected";
import { SquarePen } from "lucide-react";
function RajDashboardItem({
  title,
  Icon,
  to,
}: {
  title: string;
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  to: string;
}) {
  return (
    <NavLink to={`${to}`} className="dashboard-item">
      <Icon
        width={80}
        height={33}
        style={{
          fill: COLOR_CODES.primary,
        }}
      />
      <p>{title}</p>
    </NavLink>
  );
}

export default function RajDashboard() {
  // Clear some localStorage on mount and validate session
  const [refreshKey, setRefreshKey] = useState(0);

  const refreshSession = () => {
    setRefreshKey((prev) => prev + 1);
  };

  useEffect(() => {
    localStorage.removeItem("procurement_edit_id");
    getSessionValidity().then((res) => {
      if (res.status === false) {
        redirectToLogin(window.location.href);
      }
    });
  }, []);

  // Use searchParams to get and set the 'tab' query parameter
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "analytics";
  const [selectedTab, setSelectedTab] = useState(initialTab);

  // Sync state with query parameter changes
  useEffect(() => {
    const tab = searchParams.get("tab") || "analytics";
    if (tab !== selectedTab) {
      setSelectedTab(tab);
    }
  }, [searchParams, selectedTab]);

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
    setSearchParams({ tab });
  };

  const [dates, setDate] = useState({
    startDate: new Date("2025-01-06"),
    endDate: new Date("2025-01-13"),
    key: "selection",
  });

  return (
    <div className="page-wrapper">
      <div className="dashboard-header-between gap-x-2 flex md:justify-between items-center">
        <div className="flex flex-row md:gap-x-4 gap-x-2 items-center">
          {!isMobile && (
            <div className="me-5 mt-2">
              <Hamburger />
            </div>
          )}
          {[
            {
              key: "analytics",
              icon: AnalyticsIcon,
              selectedIcon: AnalyticsSelectedIcon,
            },
            {
              key: "dashboard",
              icon: DashboardIcon,
              selectedIcon: DashboardSelectedIcon,
            },
            {
              key: "vidyutai",
              icon: RajAgentIcon,
              selectedIcon: RajAgentSelectedIcon,
            },
          ].map(({ key, icon: Icon, selectedIcon: SelectedIcon }) =>
            selectedTab === key ? (
              <SelectedIcon
                key={key}
                width={22}
                height={22}
                onClick={() => handleTabChange(key)}
              />
            ) : (
              <Icon
                key={key}
                width={22}
                height={22}
                onClick={() => handleTabChange(key)}
              />
            )
          )}
          {selectedTab === "vidyutai" && !isMobile && (
            <h1 className="text-[18px] text-[#6F6D7C]">Vidyut AI</h1>
          )}
          {isMobile && (
            <h1 className="text-xl font-semibold tracking-wide text-gray-800">
              Vidyut AI - RUVITL
            </h1>
          )}
        </div>
        {!isMobile && (
          <div className="flex items-center gap-x-4">
            <HpgclIcon />
            <div className="flex flex-row justify-center items-center space-x-4 ">
              <h1>Power Analytics</h1>
            </div>
          </div>
        )}

        <div className="md:min-w-[300px]">
          {selectedTab === "analytics" && (
            <DynamicDatePicker
              setDate={(date) => setDate(date[0]!)}
              defaultDates={dates}
            />
          )}
          {selectedTab === "vidyutai" && (
            <div className="flex justify-end">
              <button
                onClick={refreshSession}
                title="Start New Chat"
                className="p-2 rounded hover:bg-gray-100 transition"
              >
                <SquarePen size={22} />
              </button>
            </div>
          )}
        </div>
      </div>

      {selectedTab === "analytics" ? (
        <div className="page-wrapper">
          <OverviewDas dates={dates} />
        </div>
      ) : selectedTab === "vidyutai" ? (
        <RajAgent key={refreshKey} />
      ) : (
        <div className="dashboard-item-container">
          <div className="flex flex-col justify-between h-full">
            <div className="flex flex-row flex-wrap gap-5 justify-center">
              <RajDashboardItem
                Icon={PowerAtlas}
                title="Overview"
                to="compare"
              />
              <RajDashboardItem
                Icon={DemandForecasting}
                title="Demand Analytics"
                to="demand"
              />
              <RajDashboardItem
                Icon={ConsumptionAndGenerationAnalytics}
                title="Generation Analytics"
                to="generation"
              />
              <RajDashboardItem
                Icon={ExchangeAnalysis}
                title="Power Procurement"
                to="procurement"
              />
              {/* <RajDashboardItem
                Icon={BankingAalytics}
                title="Automation"
                to="automation"
              /> */}
              <RajDashboardItem Icon={Upload} title="Upload Data" to="upload" />
              <RajDashboardItem
                Icon={GridFrequencyProfile}
                title="Transactions"
                to="procurementTable"
              />
              <RajDashboardItem
                Icon={BankingAalytics}
                title="TAM and DEEP"
                to="auctions"
              />
              <RajDashboardItem
                Icon={ExchangeAnalysis}
                title="Exchange Analytics"
                to="exchangeAnalysis"
              />
               <RajDashboardItem
                Icon={BankingAalytics}
                title="Merit Engine"
                to="merit_engine"
              />
              <RajDashboardItem
                Icon={BankingAalytics}
                title="GNA-TGNA Allocation Optimizer"
                to="optimizer"
              />
              <RajDashboardItem
                Icon={ExchangeAnalysis}
                title="PowerMarketizer"
                to="procurementOptimization"
              />
            </div>
            <div className="gna-footer-container mt-8">
              <div className="grid grid-cols-3 justify-between items-center gap-5 ">
                <h3 className="text-[#9f9f9f] font-medium">
                  GNA Energy Data and Analytics Capability Center (GDACC)s
                </h3>
                <p className="gna-footer-text text-center">
                  Powered by <strong>GNA Energy</strong>
                </p>
                <strong className="gna-footer-text  text-end">
                  Demo Version
                </strong>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const TabSwitcher: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Read the current 'tab' query parameter from the URL.
  // Default to "analytics" if no tab is specified.
  const searchParams = new URLSearchParams(location.search);
  const tab = searchParams.get("tab") || "dashboard";

  // When a tab is clicked, update the URL accordingly.
  const handleTabChange = (newTab: string) => {
    navigate(`/demo?tab=${newTab}`);
  };

  return (
    <div className="flex flex-row gap-x-2">
      {[
        {
          key: "analytics",
          icon: AnalyticsIcon,
          selectedIcon: AnalyticsSelectedIcon,
        },
        {
          key: "dashboard",
          icon: DashboardIcon,
          selectedIcon: DashboardSelectedIcon,
        },
        {
          key: "vidyutai",
          icon: RajAgentIcon,
          selectedIcon: RajAgentSelectedIcon,
        },
      ].map(({ key, icon: Icon, selectedIcon: SelectedIcon }) =>
        tab === key ? (
          <SelectedIcon
            key={key}
            width={22}
            height={22}
            onClick={() => handleTabChange(key)}
          />
        ) : (
          <Icon
            key={key}
            width={22}
            height={22}
            onClick={() => handleTabChange(key)}
          />
        )
      )}
    </div>
  );
};
